/**
 * Prompt Schema Definitions
 *
 * Spec-first schema definitions for prompt management.
 * These schemas drive oRPC type generation and validation.
 */

import { z } from "zod";
import {
  UuidField,
  TimestampField,
  MetadataField,
  ErrorResponse,
} from "./common";

// Core Prompt entity
const Prompt = z
  .object({
    promptId: UuidField,
    accountId: UuidField,
    organizationId: UuidField.nullable(),
    workspaceId: UuidField.nullable(),
    participantId: z.string().min(1).nullable(),
    key: z.string().min(1).max(255),
    refType: z.string().min(1),
    refId: z.string().min(1),
    metadata: MetadataField,
    createdAt: TimestampField,
    updatedAt: TimestampField,
  })
  .refine((data) => !data.workspaceId || data.organizationId, {
    error: "organizationId is required when workspaceId is set",
    path: ["organizationId"],
  })
  .refine((data) => !data.participantId || data.workspaceId, {
    error: "workspaceId is required when participantId is set",
    path: ["workspaceId"],
  });

// Request schemas
const UpsertPromptRequest = z
  .object({
    accountId: UuidField,
    organizationId: UuidField.optional(),
    workspaceId: UuidField.optional(),
    participantId: z.string().min(1).optional(),
    key: z.string().min(1).max(255),
    refType: z.string().min(1),
    refId: z.string().min(1),
    metadata: MetadataField,
  })
  .refine((data) => !data.workspaceId || data.organizationId, {
    error: "organizationId is required when workspaceId is set",
    path: ["organizationId"],
  })
  .refine((data) => !data.participantId || data.workspaceId, {
    error: "workspaceId is required when participantId is set",
    path: ["workspaceId"],
  });

const DeletePromptRequest = z.object({
  promptId: UuidField,
});

const GetPromptRequest = z.object({
  promptId: UuidField,
});

const GetPromptByKeyRequest = z.object({
  accountId: UuidField,
  workspaceId: UuidField.optional(),
  participantId: z.string().min(1).optional(),
  key: z.string().min(1).max(255),
});

const ListPromptsRequest = z.object({
  accountId: UuidField,
  workspaceId: UuidField.optional(),
  participantId: z.string().min(1).optional(),
});

// Event schemas
const PromptCreateEvent = z.object({
  action: z.literal("created"),
  prompt: Prompt,
  timestamp: TimestampField,
});

const PromptUpdateEvent = z.object({
  action: z.literal("updated"),
  prompt: Prompt,
  timestamp: TimestampField,
});

const PromptDeleteEvent = z.object({
  action: z.literal("deleted"),
  promptId: UuidField,
  timestamp: TimestampField,
});

// Export all schemas
export const schemas = {
  Prompt,
  UpsertPromptRequest,
  DeletePromptRequest,
  GetPromptRequest,
  GetPromptByKeyRequest,
  ListPromptsRequest,
  PromptCreateEvent,
  PromptUpdateEvent,
  PromptDeleteEvent,
  ErrorResponse,
};

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

export type { SchemaTypes as PromptSchemaTypes };
export { schemas as promptSchemas };
